export interface PromptLibraryItem {
  id: string;
  title: string;
  description: string;
  promptText: string;
}

export const promptLibrary: PromptLibraryItem[] = [
  {
    id: "review-simplify-code",
    title: "Review & Simplify Code",
    description: `Reviews and simplifies all code produced by your last requested changes. Focuses exclusively on making code cleaner, more readable, and more maintainable without altering external behavior.

**What this does:**
- Eliminates redundancy (DRY principle, built-in APIs)
- Simplifies control flow (guard clauses, pattern matching)
- Applies modern Dart/Flutter idioms (const, final, null-aware operators)
- Improves naming and readability
- Extracts reusable widgets and consolidates constants

**Safety guarantees:**
- No changes to external behavior or functionality
- Preserves error handling, validation, and null safety
- Maintains state management patterns
- Keeps lazy-loading patterns intact`,
    promptText: `Review and simplify all code produced by the last requested changes. Do not alter external behavior or functionality. The goal is exclusively to make the modified code cleaner, more readable, and more maintainable. Prioritize readable, explicit code over overly compact solutions. This is the guiding principle — clarity always wins over brevity.

**Eliminate redundancy:**
- Find and extract duplicated code into reusable functions, widgets, classes, mixins, or extensions following the DRY principle
- Replace verbose custom implementations with built-in Dart/Flutter APIs and standard library equivalents (e.g., use \`Iterable.where()\`, \`map()\`, \`fold()\` instead of manual loops; use \`ListView.separated\` instead of manual divider logic; use \`copyWith()\` instead of manually reconstructing objects)
- Consolidate similar logic patterns into a single unified approach — if two or more blocks do nearly the same thing with minor variations, merge them into one parameterized function or widget
- Eliminate redundant abstractions — remove wrapper classes, helper methods, or intermediate variables that add indirection without adding clarity

**Simplify control flow:**
- Replace complex conditional logic with guard clauses and early returns to reduce nesting
- Use Dart 3+ switch expressions and pattern matching instead of verbose if/else chains where appropriate
- IMPORTANT: Avoid nested ternary operators — prefer switch expressions, if/else chains, or extracting the logic into a named function, especially inside widget trees where chained ternaries in \`children: []\` become unreadable
- Flatten deeply nested if/else/try structures — no more than 2-3 levels of nesting
- Break large methods (more than ~30 lines of logic) into smaller, single-responsibility functions with descriptive names
- Simplify boolean expressions — remove redundant comparisons like \`== true\`, \`== false\`, \`== null\` when null-aware operators or direct checks suffice

**Apply modern Dart/Flutter idioms:**
- Use \`const\` constructors wherever possible — on widgets, EdgeInsets, colors, text styles, and any object that can be compile-time constant
- Use \`final\` for all variables that are never reassigned
- Apply null-aware operators (\`?.\`, \`??\`, \`??=\`, \`...?\`) to eliminate verbose null checks
- Use collection \`if\` and collection \`for\` inside list/widget literals instead of ternary operators or separate builder methods
- Use spread operators (\`...\`, \`...?\`) to merge lists and children cleanly
- Use records and destructuring where they reduce boilerplate
- Use trailing commas consistently for better formatting and diffs
- Prefer \`context.watch<T>()\` / \`context.read<T>()\` over deeply nested \`Consumer\` or \`Builder\` widgets when using Provider
- Use \`switch\` expressions for mapping enums or types to widgets/values
- Replace verbose patterns with concise, expressive alternatives (e.g., \`?.let()\`, cascade notation \`..\`, \`Iterable.firstWhereOrNull()\`)

**Improve naming and readability:**
- Rename variables, functions, parameters, and classes to be descriptive and self-documenting — avoid abbreviations unless universally understood (\`id\`, \`url\`, \`max\`)
- Remove comments that restate what the code already says — code should be self-explanatory
- Keep and improve comments that explain *why* a decision was made, not *what* the code does
- Group related fields and methods together logically within a class
- Consolidate related logic so readers can understand a feature in one place rather than jumping across files

**Improve structure:**
- Extract reusable widgets into separate classes when a \`build()\` method exceeds ~40 lines or when a subtree is used in multiple places
- Separate business logic from UI code — state calculations, data transformations, and validation logic should not live inside \`build()\` methods
- Apply proper encapsulation — make fields and methods private (\`_\`) when they are not accessed from outside the class or file
- Extract shared constants, magic numbers, repeated strings, styles, and theme values into centralized locations (constants file, theme extensions, or top-level \`const\` declarations)
- Identify opportunities to use extensions on existing types for reusable utility methods instead of standalone helper functions

**Maintain balance — do not over-simplify:**
- Do not combine too many concerns into a single function or widget — each should have a clear single responsibility
- Do not remove helpful abstractions that improve code organization just to reduce file count or line count
- Do not prioritize "fewer lines" over readability — dense one-liners, chained cascades, or deeply nested expressions that require mental parsing are worse than a few extra lines of clear code
- Do not create overly clever solutions that are hard to understand — future developers must be able to read and modify the code easily
- Do not make the code harder to debug or extend in pursuit of elegance

**Safety constraints — do not violate any of these:**
- Do not change any external behavior or functionality beyond what the last change request specified
- Do not rename public class names, public method signatures, or public property names that other files depend on — unless every reference across the entire codebase is also updated
- Do not remove or weaken any error handling, try/catch blocks, or null safety checks
- Do not remove any existing input validation or boundary checks
- Do not change widget keys that are used for state preservation
- Do not replace \`ListView.builder\` or \`ListView.separated\` with \`Column(children: [...])\` for long or dynamic lists — keep lazy-loading patterns intact
- Do not remove \`const\` from any widget that is already \`const\`
- Do not introduce new package dependencies
- Do not change state management patterns (e.g., do not convert a \`StatefulWidget\` to \`StatelessWidget\` or change the state management approach unless the change request explicitly asked for it)
- Preserve all existing test expectations — if tests exist for the modified code, the refactored version must pass them identically
- When choosing between a clever/terse solution and a clear/readable solution, always choose clear
- If uncertain whether a simplification is safe, leave the original code as-is

**Verification and summary:**
After simplifying, confirm that every public method, widget, callback, and navigation flow in the modified files behaves identically to the pre-simplification version. The simplified code must be a drop-in replacement with zero behavioral difference. Document only significant changes that affect understanding — for each file modified, briefly note what was simplified and the technique applied (e.g., "extracted repeated padding into shared constant", "replaced nested ternaries with switch expression", "consolidated two similar list tiles into parameterized widget").`,
  },
  {
    id: "firebase-analytics-minimal",
    title: "Firebase Analytics (Minimal Setup)",
    description: `Sets up Firebase Analytics without gradle plugin or google-services.json. Uses direct FirebaseOptions initialization from JSON config.

**IMPORTANT:**

Before sending this prompt, you must copy-paste your complete google-services.json file content where indicated in the prompt.

To get the google-services.json file: Go to your Firebase Console → Project Settings → General tab → Under "Your apps" section, download the google-services.json file for your Android app.

Note: While google-services.json is Android-specific, this minimal setup extracts the Firebase configuration values from it and uses them to initialize Firebase for all platforms (Android, iOS, and web) without needing platform-specific config files.`,
    promptText: `Integrate Firebase Analytics only (no google-services.json, no Gradle plugin); add firebase_core + firebase_analytics; in main.dart call WidgetsFlutterBinding.ensureInitialized(); then await Firebase.initializeApp(options: FirebaseOptions(...)) using values from the JSON below; after initialization call FirebaseAnalytics.instance.setAnalyticsCollectionEnabled(true); if initialization fails catch and log error and continue to runApp; create android/app/src/main/res/values/analytics.xml with <string name="google_app_id">…</string> (and <string name="ga_app_id">…</string> if present); ensure initialization logic covers web + Android + iOS (use kIsWeb or Platform.isAndroid / Platform.isIOS) so Firebase.initializeApp runs on each; nothing else. Use this JSON for config:

=== JSON START ===
[paste google-services.json here]
=== JSON END ===`,
  },
  {
    id: "admob-consent-flow",
    title: "AdMob Consent Flow (GDPR/Privacy)",
    description: `Sets up Google AdMob consent management using google_mobile_ads package only. Handles consent UI, non-personalized ads configuration, and ensures SDK initialization only after consent is obtained. No ad placements yet.

**IMPORTANT:**

For this consent flow to work, you must configure consent settings in your AdMob account under Privacy & messaging (Privacy & Settings section).`,
    promptText: `Use only the google_mobile_ads package (do not include or reference user_messaging_platform).

On app startup:
• Call ConsentInformation.instance.requestConsentInfoUpdate() (with ConsentRequestParameters) to get the latest consent state.
• Then call ConsentForm.loadAndShowConsentFormIfRequired() (or equivalent) to show the consent UI if required.
Do not call MobileAds.instance.initialize() until the consent flow has completed (either the form was shown and responded to, or it was determined no form was required).

After the consent flow:
• If await ConsentInformation.instance.canRequestAds() returns true, call MobileAds.instance.initialize().
• If consent is not granted (or the user opted out of personalised ads), configure for non-personalised ads (e.g., AdRequest(nonPersonalizedAds: true)) before or at initialization.
Before any ad requests, always check canRequestAds().
In the AndroidManifest.xml, include the AdMob Application ID meta-tag (for example the official test ID) as required by the plugin and platform setup.

At this stage do not implement any ad placements (e.g., banners, interstitials) — focus only on the consent logic and SDK initialization.
Ensure that the consent flow is asynchronous and does not block the entire UI — allow the app's UI to load smoothly while awaiting or handling the consent result.This ensures that ads will only load after the user has either granted or declined consent, keeping your app compliant.`,
  },
  {
    id: "banner-ad-main-screen",
    title: "AdMob Banner Ad on Main Screen",
    description: `Displays an AdMob banner ad at the top of the main screen. Uses test ad unit ID for development and testing.

**IMPORTANT:**

This prompt uses test ad unit IDs. Before releasing your production build, ask Darvin to replace the test ad unit IDs with your production ad unit IDs from AdMob.`,
    promptText: `Add an AdMob banner ad at the top of the main screen.
Use the official AdMob banner ad unit test ID.
Ensure the banner is visible and properly positioned at the top of the screen without overlapping other UI elements.`,
  },
  {
    id: "interstitial-ad-game-over",
    title: "AdMob Interstitial Ad After Game Over",
    description: `Displays AdMob interstitial ad immediately after game ends, before game over popup. Uses test ad unit ID.

Note: "Game over" is used as an example placement. You can modify the prompt to show the interstitial ad at any other placement in your app (e.g., after completing a level, before navigating to a new screen, etc.).

**IMPORTANT:**

This prompt uses test ad unit IDs. Before releasing your production build, ask Darvin to replace the test ad unit IDs with your production ad unit IDs from AdMob.`,
    promptText: `Show AdMob interstitial ad immediately after the game ends, but before displaying the game over popup.
Use the official AdMob interstitial ad unit test ID.`,
  },
  // # Disabled temporarily until implementing grep and fixing issues with Flutter Web packages removal
  //   {
  //     id: "cleanup-unused-dependencies",
  //     title: "Clean Up Unused Dependencies",
  //     description: `Analyzes your entire Flutter/Dart codebase and updates pubspec.yaml to remove all unused dependencies. Keeps the Flutter SDK and only packages that are actually imported and required.

  // **What this does:**
  // - Scans all Dart files for actual import statements
  // - Identifies packages in pubspec.yaml that are never imported
  // - Removes unused dependencies while preserving required ones
  // - Outputs a valid pubspec.yaml that works with \`flutter pub get\`

  // **What this does NOT do:**
  // - Add new packages
  // - Change package versions
  // - Modify any Dart files
  // - Output anything except the updated pubspec.yaml`,
  //     promptText: `Clean up the Flutter project by analyzing the entire Dart codebase and updating pubspec.yaml to remove all unused dependencies while keeping the Flutter SDK and only packages that are actually imported and required, without adding new packages, changing versions, modifying Dart files, or outputting anything except a valid updated pubspec.yaml that works with flutter pub get.`,
  //   },
];
